// The automatic move of pre-routes (spec §3.12): what `shared/src/routing/pre-route.ts` decides,
// written in the session of whoever has a routing page open, on every refresh of the pre-routes.
//
// OUTSIDE-PLATFORM: P17 the platform has no system actor that can create or update a CRUD record
// on its own (a timer, a source load), so the server cannot write it. This file is the one place
// that does; with P17 the server runs the same plan and this file goes. The System log says
// "Pre-route (automatic) — applied in <person>'s session", never as if that person typed it.
//
// - Claim first: the pre-route is saved Moved with its version (`expect` movedAt), so of two tabs
//   one wins and the other does nothing. A claim never finished (the tab closed) is taken again
//   after 5 minutes (`nextStep`).
// - Then each line: a line nobody saved yet is created the normal way first (`ensureStored`); only
//   blank fields are written, checked against the line as saved now; a filled one is kept and said.
// - Then the pre-route is done: the lines it moved to, and what they kept, in its System log entry.

import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  NO_HOLIDAYS,
  NO_PTS,
  PRE_ROUTES,
  ROUTING_LINES,
  asDay,
  carryInput,
  fieldChange,
  fieldLabel,
  isBlankEntry,
  keptText,
  localToday,
  moveNote,
  movePlan,
  nextStep,
  routingRows,
} from "@app/shared";
import type { KeptValue, LineView, PreRouteRecord, WarehouseField } from "@app/shared";
import { crudApi, useMe } from "@axny/platform-ui";
import { PRE_ROUTES_KEY, useHolidays, usePreRoutes } from "./data";
import { MessageProvider, notify } from "./message";
import { lineText, lostToAnother, useWriter } from "./writer";

/**
 * Mounted once, by the shell, on every routing page of a person with a Role. Renders nothing.
 * `VITE_PRE_ROUTE_MOVER=off` (set when starting `pnpm dev`) turns it off: a local app against the
 * staging database must not move pre-routes beside the deployed staging app.
 */
export function PreRouteMover() {
  if (import.meta.env.VITE_PRE_ROUTE_MOVER === "off") return null;
  return (
    <MessageProvider>
      <Mover />
    </MessageProvider>
  );
}

const shown = (v: unknown) => (v === null || v === undefined ? "" : (asDay(v) ?? String(v)));

function Mover() {
  const today = useMemo(() => localToday(), []);
  const me = useMe();
  const who = me.data?.authenticated ? me.data.user.displayName || me.data.user.username : "";
  const data = usePreRoutes();
  const holidays = useHolidays();
  const writer = useWriter(PRE_ROUTES_KEY, who, today);
  const queryClient = useQueryClient();
  const running = useRef(false);
  const kit = writer.kit;

  useEffect(() => {
    const payload = data.data;
    if (!payload || !who || running.current || holidays.isPending) return;
    const due = payload.preRoutes
      .map((r) => ({ r, step: nextStep(r, payload.orders[r.orderNo] ?? null, payload.orderPts[r.orderNo] ?? NO_PTS) }))
      .filter((x) => x.step.kind === "move" || x.step.kind === "close");
    if (!due.length) return;
    running.current = true;
    const lines = routingRows(payload, today, holidays.data?.calendar ?? NO_HOLIDAYS).assembled.lines;
    void (async () => {
      let wrote = false;
      for (const { r, step } of due) {
        try {
          if (step.kind === "close") wrote = (await close(r, step)) || wrote;
          else wrote = (await move(r, lines)) || wrote;
        } catch (e) {
          // Another session saved it first (409, or its claim is newer): nothing to do here.
          if (lostToAnother(e)) continue;
          notify(
            `Pre-route ${r.orderNo} didn't finish moving`,
            "It is tried again on the next refresh, from any open routing page.",
            6000,
          );
        }
      }
      running.current = false;
      if (wrote) await queryClient.invalidateQueries({ queryKey: ["routing"] });
    })();
    // A new answer of the pre-routes is the trigger; the rest is read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.data, who, holidays.isPending]);

  /** A2000 closed the order with no pick ticket: Cancelled or Shipped, once (the version decides). */
  const close = async (r: PreRouteRecord, step: Extract<ReturnType<typeof nextStep>, { kind: "close" }>) => {
    const on = step.canceledOn ? ` on ${step.canceledOn.slice(5, 7)}/${step.canceledOn.slice(8, 10)}` : "";
    await kit.put(
      PRE_ROUTES,
      r.id,
      { status: step.status, orderStatus: step.orderStatus, canceledOn: step.canceledOn, cancelCode: step.cancelCode },
      {
        action: "pre-route",
        lineText: `${r.customerName || "—"} · Pre-route ${r.orderNo}`,
        changes: fieldChange("status", r.status, step.status),
        note:
          r.source === "Deposco"
            ? `SO ${r.orderNo} ${step.status === "Shipped" ? "shipped" : "canceled"} in Deposco (${step.orderStatus}) before its line was on the Worklist — ${moveNote(who)}`
            : `Order ${r.orderNo} ${step.status === "Shipped" ? "shipped" : "cancelled"} in A2000 (${step.orderStatus})${on} with no pick ticket — ${moveNote(who)}`,
      },
      { field: "status", value: "Waiting" },
    );
    return true;
  };

  /** Claim, write the lines, finish. False when the claim went to another session. */
  const move = async (r: PreRouteRecord, lines: readonly LineView[]) => {
    const plan = movePlan(r, lines, today);
    if (!plan.length) return false;
    // The claim: Moved, with this session's time; `expect` makes a second tab's claim fail.
    await kit.put(
      PRE_ROUTES,
      r.id,
      { status: "Moved", movedAt: new Date().toISOString(), moveDone: "No" },
      { partOf: `pre-route ${r.orderNo}` },
      { field: "movedAt", value: r.movedAt },
    );
    const ids: string[] = [];
    const kept: { line: string; k: KeptValue }[] = [];
    for (const step of plan) {
      const name = lineText(step.line);
      const id = await kit.chained(kit.keyOf(step.line), async () => {
        const existing = kit.storedId(step.line);
        // The line as saved now: a field filled since the page read it is kept, not written over.
        const now = existing ? await crudApi.get(ROUTING_LINES.id, existing) : null;
        const change = { ...step.change } as Record<string, unknown>;
        for (const k of step.kept) kept.push({ line: name, k });
        if (now) {
          for (const f of Object.keys(change) as WarehouseField[]) {
            const saved = shown(now[f]);
            if (f === "routingNotes" || isBlankEntry(f, saved) || saved === shown(change[f])) continue;
            if (f !== "routedOn")
              kept.push({
                line: name,
                k: { field: f, label: fieldLabel(ROUTING_LINES.id, f), line: saved, preRoute: shown(change[f]) },
              });
            delete change[f];
          }
          // Notes typed since: the pre-route's line goes after them, once.
          const notes = shown(now.routingNotes);
          const note = `Pre-route: ${r.notes.trim()}`;
          if (change.routingNotes !== undefined)
            if (notes.includes(note)) delete change.routingNotes;
            else change.routingNotes = (notes ? `${notes} · ${note}` : note).slice(0, 500);
        }
        const changes = Object.entries(change).flatMap(([f, v]) =>
          fieldChange(f, now ? shown(now[f]) : step.line.fields[f as WarehouseField], v),
        );
        const log = {
          action: "pre-route" as const,
          lineText: name,
          changes,
          note: `${moveNote(who)} · from pre-route ${r.orderNo}`,
        };
        const full = { ...(step.line.carry.sources.length ? carryInput(step.line) : {}), ...change };
        const made =
          existing && !changes.length
            ? { id: existing, createdNow: false }
            : await kit.ensureStored(step.line, full, log);
        if (!made.createdNow && changes.length) kit.keep([await kit.put(ROUTING_LINES, made.id, full, log)]);
        return made.id;
      });
      ids.push(id);
    }
    const keptNote = kept.length
      ? ` · Kept on the line: ${kept.map((x) => `${x.line}: ${keptText(x.k)}`).join("; ")}`
      : "";
    await kit.put(
      PRE_ROUTES,
      r.id,
      { movedLineIds: ids.join(",").slice(0, 500), moveDone: "Yes" },
      {
        action: "pre-route",
        lineText: `${r.customerName || "—"} · Pre-route ${r.orderNo}`,
        changes: [...fieldChange("status", "Waiting", "Moved"), ...fieldChange("movedLineIds", "", ids.join(", "))],
        note: `Moved to ${ids.length} Worklist line${ids.length === 1 ? "" : "s"} — ${moveNote(who)}${keptNote}`.slice(
          0,
          500,
        ),
      },
    );
    return true;
  };

  return null;
}
